import { useCallback, useContext, useEffect, useRef } from 'react';

import { ExperienceTrackerContext } from '@atlassian/experience-tracker/component/ExperienceTrackerContext';

import { useAIMateExperienceTracker } from './main';
import {
	beginRemixEmbedRender,
	completeRemixEmbedRender,
	failRemixEmbedRender,
	releaseRemixEmbedRender,
} from './remix-view-experience';

/** Outer bound for one Remix image or infographic to display. */
export const REMIX_MEDIA_RENDER_TIMEOUT_MS = 35_000;

export type MediaRenderEvent = {
	dataConsumerSource?: string;
	mediaId?: string;
	mediaInstance: object;
} & (
	| { type: 'mounted' | 'unmounted' }
	| { renderedMediaId?: string; type: 'preview-rendered' }
	| { reason: string; type: 'error' }
);

type RemixMediaMetadata = {
	remixSubtype?: string;
	remixType?: 'image' | 'infographic';
};

type PendingRenderOutcome =
	| { type: 'success'; renderedMediaId?: string }
	| { type: 'failure'; reason: string };

type TrackedMedia = RemixMediaMetadata & {
	mediaId: string;
	registrationId: string;
	settled: boolean;
	timeout: ReturnType<typeof setTimeout>;
};

/** Parses the persistent ADF dataConsumer marker written for Remix-generated media. */
const parseRemixMediaSource = (source?: string): RemixMediaMetadata | undefined => {
	if (source === 'remix') {
		return {};
	}
	if (!source) {
		return undefined;
	}

	const [prefix, remixType, remixSubtype, ...rest] = source.split(':');
	if (
		prefix !== 'remix' ||
		(remixType !== 'image' && remixType !== 'infographic') ||
		!remixSubtype ||
		rest.length > 0
	) {
		return undefined;
	}

	return { remixSubtype, remixType };
};

/**
 * Creates the product-owned callback passed to Atlaskit's renderer and media
 * plugin. Atlaskit reports generic media lifecycle events; this controller
 * selects persisted Remix markers and adds them to the shared page experience.
 */
export const useRemixViewMediaExperience = ({
	contentId,
	enabled,
	pageMode,
}: {
	contentId?: string;
	enabled: boolean;
	pageMode: 'edit' | 'live' | 'view';
}): { onMediaRenderEvent?: (event: MediaRenderEvent) => void } => {
	const experienceTracker = useAIMateExperienceTracker();
	const pageSessionKey = useContext(ExperienceTrackerContext);
	const trackedMediaRef = useRef(new Map<object, TrackedMedia>());
	const pendingOutcomesRef = useRef(new Map<object, PendingRenderOutcome>());
	const nextRegistrationIdRef = useRef(0);

	const release = useCallback(
		(mediaInstance: object) => {
			pendingOutcomesRef.current.delete(mediaInstance);
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia) {
				return;
			}

			clearTimeout(trackedMedia.timeout);
			trackedMediaRef.current.delete(mediaInstance);
			releaseRemixEmbedRender({
				embedId: trackedMedia.registrationId,
				experienceTracker,
				pageSessionId: contentId,
				pageSessionKey,
			});
		},
		[contentId, experienceTracker, pageSessionKey],
	);

	const succeed = useCallback(
		(mediaInstance: object, renderedMediaId?: string) => {
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia) {
				pendingOutcomesRef.current.set(mediaInstance, {
					renderedMediaId,
					type: 'success',
				});
				return;
			}
			if (
				trackedMedia.settled ||
				(renderedMediaId !== undefined && renderedMediaId !== trackedMedia.mediaId)
			) {
				return;
			}

			trackedMedia.settled = true;
			clearTimeout(trackedMedia.timeout);
			completeRemixEmbedRender({
				embedId: trackedMedia.registrationId,
				experienceTracker,
				pageSessionId: contentId,
				pageSessionKey,
			});
		},
		[contentId, experienceTracker, pageSessionKey],
	);

	const fail = useCallback(
		(mediaInstance: object, reason: string, isTimeout = false) => {
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia) {
				pendingOutcomesRef.current.set(mediaInstance, { reason, type: 'failure' });
				return;
			}
			if (trackedMedia.settled) {
				return;
			}

			trackedMedia.settled = true;
			clearTimeout(trackedMedia.timeout);
			const errorType = isTimeout ? 'render_timeout' : 'media_render_error';
			failRemixEmbedRender({
				attributes: {
					failedRemixSubtype: trackedMedia.remixSubtype,
					failedRemixType: trackedMedia.remixType,
					renderFailureReason: errorType,
				},
				embedId: trackedMedia.registrationId,
				error: new Error(reason),
				errorType,
				experienceTracker,
				pageSessionId: contentId,
				pageSessionKey,
			});
		},
		[contentId, experienceTracker, pageSessionKey],
	);

	const trackMediaRenderEvent = useCallback(
		(event: MediaRenderEvent) => {
			if (event.type === 'unmounted') {
				release(event.mediaInstance);
				return;
			}
			if (event.type === 'preview-rendered') {
				succeed(event.mediaInstance, event.renderedMediaId);
				return;
			}
			if (event.type === 'error') {
				fail(event.mediaInstance, event.reason);
				return;
			}

			const remixMetadata = parseRemixMediaSource(event.dataConsumerSource);
			if (!event.mediaId || !remixMetadata) {
				pendingOutcomesRef.current.delete(event.mediaInstance);
				return;
			}
			if (trackedMediaRef.current.has(event.mediaInstance)) {
				return;
			}

			const registrationId = `media:${event.mediaId}:${nextRegistrationIdRef.current++}`;
			beginRemixEmbedRender({
				attributes: {
					contentType:
						pageMode === 'live'
							? 'live-doc'
							: pageMode === 'edit'
								? 'classic-page-edit'
								: 'classic-page-view',
					pageMode,
					product: 'confluence',
					remixSubtype: remixMetadata.remixSubtype,
					remixType: remixMetadata.remixType,
				},
				embedId: registrationId,
				experienceTracker,
				pageSessionId: contentId,
				pageSessionKey,
			});

			const timeout = setTimeout(() => {
				fail(event.mediaInstance, 'Remix media did not render within the expected time', true);
			}, REMIX_MEDIA_RENDER_TIMEOUT_MS);
			trackedMediaRef.current.set(event.mediaInstance, {
				...remixMetadata,
				mediaId: event.mediaId,
				registrationId,
				settled: false,
				timeout,
			});

			const pendingOutcome = pendingOutcomesRef.current.get(event.mediaInstance);
			pendingOutcomesRef.current.delete(event.mediaInstance);
			if (pendingOutcome?.type === 'success') {
				succeed(event.mediaInstance, pendingOutcome.renderedMediaId);
			} else if (pendingOutcome?.type === 'failure') {
				fail(event.mediaInstance, pendingOutcome.reason);
			}
		},
		[contentId, experienceTracker, fail, pageMode, pageSessionKey, release, succeed],
	);
	const onMediaRenderEvent = useCallback(
		(event: MediaRenderEvent) => {
			if (!enabled) {
				return;
			}
			try {
				trackMediaRenderEvent(event);
			} catch {
				// Reliability instrumentation must never interrupt the media rendering lifecycle.
				return;
			}
		},
		[enabled, trackMediaRenderEvent],
	);

	useEffect(
		() => {
			if (!enabled) {
				return;
			}
			const trackedMedia = trackedMediaRef.current;
			const pendingOutcomes = pendingOutcomesRef.current;

			return () => {
				for (const mediaInstance of trackedMedia.keys()) {
					onMediaRenderEvent({ mediaInstance, type: 'unmounted' });
				}
				pendingOutcomes.clear();
			};
		},
		[enabled, onMediaRenderEvent],
	);

	return { onMediaRenderEvent: enabled ? onMediaRenderEvent : undefined };
};
